import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { HttpClient, provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import Aura from '@primeng/themes/aura';
import { CoreModule } from './core/core.module';
import { HttpLoaderFactory } from './core/loaders/translate.loader';
import { KeycloakService } from 'keycloak-angular';
import { keycloakInterceptor } from './core/services/interceptors/keycloak.interceptor';
// Keycloak imports
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
// PrimeNG imports
import { provideRouter, Router, withInMemoryScrolling, withPreloading } from '@angular/router';
import { routes } from './app.routes';
import { SelectivePreloadStrategy } from './core/strategies/selective-preload.strategy';
import { spaceInterceptor } from './core/services/interceptors/space.interceptor';
import { GlobalErrorHandler } from './core/handlers/global-error.handler';
import { DEV_TOOLS_TAB_COMPONENTS } from './core/dev-tools/dev-tools.types';
import { devToolsHttpInterceptor } from './core/dev-tools/http-interceptor';
import { DevToolsFormInjectTabComponent } from './core/dev-tools/tabs/form-inject-tab.component';
import { DevToolsHttpTabComponent } from './core/dev-tools/tabs/http-tab.component';
import { DevToolsStateTabComponent } from './core/dev-tools/tabs/state-tab.component';
import { DevToolsWsTabComponent } from './core/dev-tools/tabs/ws-tab.component';
import { DevToolsStateMachineTabComponent } from './core/dev-tools/tabs/state-machine-tab.component';
import { DevToolsPaymentTabComponent } from './core/dev-tools/tabs/payment-tab.component';
import { DevToolsStreamTabComponent } from './core/dev-tools/tabs/stream-tab.component';
import { StreamTrackerService } from './core/dev-tools/stream-tracker.service';
import { AnalyticsService } from './core/services/analytics/analytics.service';
import { registerGndadFileComponent } from './core/services/formio/gndad-file.component';
import { GuestSessionPurgeService } from './core/services/auth/guest-session-purge.service';
import { exposeAppInfoConsole } from './core/utils/app-info-console.util';
import * as Sentry from '@sentry/angular';

export const appConfig: ApplicationConfig = {
  providers: [
    // Core Angular Providers
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
      // Préchargement opt-in (data: { preload: true }) sur idle uniquement —
      // PreloadAllModules téléchargeait tous les chunks lazy en arrière-plan.
      withPreloading(SelectivePreloadStrategy)
    ),
    // FormioAppConfig n'est plus fourni ici : importer @formio/angular depuis
    // le graphe eager embarquait @formio/js (~1,7 MB) dans le bundle initial.
    // Voir core/services/formio/formio-app-config.provider.ts (fourni au niveau
    // des composants formio-loader / formio-wrapper).

    // Translation Module
    importProvidersFrom(
      CoreModule,
      TranslateModule.forRoot({
        fallbackLang: 'fr',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
        }
      })
    ),

    // Animations
    provideAnimationsAsync(),

    // PrimeNG Configuration
    providePrimeNG({
      theme: {
        preset: Aura
      }
    }),

    // HTTP Client with Interceptors
    // - withInterceptors : intercepteurs fonctionnels (keycloakInterceptor, spaceInterceptor)
    // - withInterceptorsFromDi : intercepteurs classe déclarés dans CoreModule
    //   (LanguageInterceptor, ErrorInterceptor) — sans cette ligne ils sont inertes.
    provideHttpClient(
      withFetch(),
      // devToolsHttpInterceptor en dernier : il voit la requête finale (headers
      // keycloak/space inclus) et trace tout dans l'onglet HTTP du DevTools.
      // No-op complet en production (DevToolsService.enabled = false).
      withInterceptors([keycloakInterceptor, spaceInterceptor, devToolsHttpInterceptor]),
      withInterceptorsFromDi()
    ),

    // 🔐 Keycloak Configuration
    KeycloakService,
    //  KeycloakInitializerProvider  // ⚠️ DÉSACTIVÉ - Keycloak s'initialise via authGuard uniquement

    // Global error handler (chunk loading failures → auto-reload)
    { provide: ErrorHandler, useClass: GlobalErrorHandler },

    // Sentry TraceService : instrumente le Router pour les spans de
    // navigation. Inerte si Sentry.init n'a pas été appelé (DSN vide).
    { provide: Sentry.TraceService, deps: [Router] },

    // DevTools — traceur de flux (fetch/SSE + EventSource).
    //
    // Posé avant tout le reste : une connexion ouverte avant l'installation
    // reste invisible, exactement comme une connexion ouverte avant l'ouverture
    // de l'onglet Network. Le chat rouvre son EventSource dès la restauration
    // de session, donc au chargement de la page.
    //
    // No-op complet en production et en SSR (DevToolsService.enabled = false) :
    // window.fetch n'est alors jamais remplacé.
    provideAppInitializer(() => {
      inject(StreamTrackerService).install();
    }),

    // Google Analytics — no-op hors production (GA_MEASUREMENT_ID vide) et
    // sans consentement "statistics" ; voir AnalyticsService.
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
      inject(AnalyticsService);
      // Assainissement des jetons invités fantômes — périmés ou orphelins —
      // avant que le moindre composant ne lise le stockage. `sessionStorage`
      // survit des jours dès lors que le navigateur restaure ses onglets : sans
      // ce balayage, une session morte continue d'alimenter la page de paiement
      // invitée. Voir GuestSessionPurgeService.
      inject(GuestSessionPurgeService).purgeStaleOnStartup();
      // Commandes console version/changelog (__APP__.version(), __APP__.changelog())
      // — disponibles dans tous les environnements, prod comprise.
      exposeAppInfoConsole();
    }),

    // Champ fichier au Design System GNDAD, enregistré sous la clé `file`.
    //
    // Au démarrage plutôt qu'au premier rendu : les formulaires sont montés
    // depuis plusieurs hôtes (`formio-form-advanced`, `formio-loader`,
    // `form-preview-modal`), et un enregistrement posé dans l'un d'eux
    // laisserait les autres sur le gabarit natif. L'appel est idempotent et
    // n'a aucun effet si `@formio/js` n'expose pas le composant natif.
    provideAppInitializer(() => {
      registerGndadFileComponent();
    }),

    // DevTools — onglet HTTP (trace toutes les requêtes HttpClient, toutes pages)
    {
      provide: DEV_TOOLS_TAB_COMPONENTS,
      multi: true,
      useValue: {
        tab: { id: 'http', label: 'HTTP', icon: 'ph ph-arrows-left-right', order: 5 },
        component: DevToolsHttpTabComponent,
      },
    },

    // DevTools — onglet Injection (cycle de vie de gnspdTreatmentInjectedVariable)
    {
      provide: DEV_TOOLS_TAB_COMPONENTS,
      multi: true,
      useValue: {
        tab: { id: 'form-inject', label: 'Injection', icon: 'ph ph-syringe', order: 8 },
        component: DevToolsFormInjectTabComponent,
      },
    },

    // DevTools — onglet State Inspector (dev-only, no-op en prod via DevToolsService.enabled)
    {
      provide: DEV_TOOLS_TAB_COMPONENTS,
      multi: true,
      useValue: {
        tab: { id: 'state', label: 'State', icon: 'ph ph-database', order: 10 },
        component: DevToolsStateTabComponent,
      },
    },

    // DevTools — onglet WebSocket Monitor
    {
      provide: DEV_TOOLS_TAB_COMPONENTS,
      multi: true,
      useValue: {
        tab: { id: 'ws', label: 'WebSocket', icon: 'ph ph-broadcast', order: 30 },
        component: DevToolsWsTabComponent,
      },
    },

    // DevTools — onglet State Machine Debugger (pipeline getTaskAction)
    {
      provide: DEV_TOOLS_TAB_COMPONENTS,
      multi: true,
      useValue: {
        tab: { id: 'state-machine', label: 'State Machine', icon: 'ph ph-graph', order: 20 },
        component: DevToolsStateMachineTabComponent,
      },
    },

    // DevTools — onglet Stream (fetch/SSE + EventSource, invisibles ailleurs)
    {
      provide: DEV_TOOLS_TAB_COMPONENTS,
      multi: true,
      useValue: {
        tab: { id: 'stream', label: 'Stream', icon: 'ph ph-waveform', order: 35 },
        component: DevToolsStreamTabComponent,
      },
    },

    // DevTools — onglet Payment Matcher Inspector (timeline task↔payment, cross-match detection)
    {
      provide: DEV_TOOLS_TAB_COMPONENTS,
      multi: true,
      useValue: {
        tab: { id: 'payment', label: 'Payment', icon: 'ph ph-credit-card', order: 40 },
        component: DevToolsPaymentTabComponent,
      },
    },
  ]
};
